import React, { useState, useEffect } from 'react';
import axios from 'axios';
import qs from 'qs';
import clsx from 'clsx';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

import Loading from '../../common_components/Loading';
import {
  CorelationTabContent,
  LineRegressionTabContent,
  QqplotTabContent,
  NormStatisticsTabContent,
} from './TabContents';
import { FileChanger } from './controls/FileChanger';
import { DataPreviewer } from './controls/DataPreviewer';
import { Select } from './inputs/Select';
import { TextInput } from './inputs/Text';
import { MultiSelect } from './inputs/MultiSelect';
import { EquationsField } from './inputs/EquationsField';
import { getFileNameFromPath } from '../../utils';

const statisticalMethods = [
  {
    label: 'Testy Normalności',
    value: 'norm_statistic',
  },
  {
    label: 'Qqplot',
    value: 'qqplot',
  },
  {
    label: 'Korelacja',
    value: 'correlation',
  },
  {
    label: 'Regresja Liniowa',
    value: 'line_regression',
  },
];
const componentMapping = {
  qqplot: QqplotTabContent,
  correlation: CorelationTabContent,
  norm_statistic: NormStatisticsTabContent,
};
const initialMainPanelData = {
  norm_statistic: null,
  correlation: null,
  qqplot: null,
  line_regression: {
    plot: null,
    metrics: null,
    assumptionTests: null,
    evaluation: null,
  },
};

export const AnalysisPanel = ({
  projectData,
  setProjectData,
  setShowingWelcomeScreen,
}) => {
  const [analysisMethod, setAnalysisMethod] = useState('norm_statistic');
  const [visualizationTab, setVisualizationTab] = useState('plot');
  const [mainPanelData, setMainPanelData] = useState(initialMainPanelData);
  const [isLoading, setIsLoading] = useState(false);
  const sideFieldsRenderingData = getStepDataFor(
    projectData?.steps,
    analysisMethod,
  );
  const [selectedSideFields, setSelectedSideFields] = useState(() =>
    getDefaultSelectedFields(projectData),
  );
  const isSidePanelDataSet = (analysisMethod, selectedSideFields) => {
    return !(
      analysisMethod == 'line_regression' &&
      (selectedSideFields.line_regression === null ||
        selectedSideFields.line_regression?.length === 0 ||
        !(selectedSideFields.line_regression?.equations?.length > 0))
    );
  };

  // filtering params
  const filterFieldRenderingData = projectData.filtering_field;
  const [filterParams, setFilterParams] = useState({
    column: filterFieldRenderingData.default_value,
    value: getPossibleDependentOptions(
      filterFieldRenderingData.default_value,
      filterFieldRenderingData.dependent_field.values,
    )[0].value,
  });

  const NonLrComponent = componentMapping[analysisMethod];

  const fetchData = (
    visualizationTab,
    analysisMethod,
    filterParams,
    adjustParams,
  ) => {
    setIsLoading(true);

    const formSubmitUrl = getFormSubmitUrl(analysisMethod, visualizationTab);
    const responseType = getResponseType(analysisMethod, visualizationTab);
    const params = adjustRequestParams(analysisMethod, {
      ...filterParams,
      ...adjustParams,
    });

    axios
      .get(formSubmitUrl, {
        responseType,
        params,
        paramsSerializer: (params) =>
          qs.stringify(
            params,
            // convert array params to Django's default format
            { arrayFormat: 'repeat' },
          ),
      })
      .then((res) => {
        setMainPanelData((p) =>
          analysisMethod === 'line_regression'
            ? {
                ...p,
                [analysisMethod]: {
                  ...p[analysisMethod],
                  // in line_regression we need to account for visualizationTab
                  [visualizationTab]: res.data,
                },
              }
            : { ...p, [analysisMethod]: res.data },
        );
        setIsLoading(false);
      });
  };
  useEffect(() => {
    fetchData(
      visualizationTab,
      analysisMethod,
      filterParams,
      selectedSideFields[analysisMethod],
    );
  }, []);

  // debounce requests on text input changes
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchData(
        visualizationTab,
        analysisMethod,
        filterParams,
        selectedSideFields[analysisMethod],
      );
    }, 1000);

    return () => clearTimeout(handler);
  }, [
    selectedSideFields.qqplot?.title,
    selectedSideFields.qqplot?.x_label,
    selectedSideFields.qqplot?.y_label,
  ]);

  return (
    <div className="[--app-radius:0.5rem] [--border-color:oklch(var(--b3))] w-full [&_.app-side-content]:bg-base-300 [&_.app-side-content_*]:text-black shadow-lg">
      {/* top panel */}
      <div className="flex bg-base-300 max-w-[--page-content-w] mx-auto rounded-t-[--app-radius] p-4 pb-6 pr-12 items-center shadow-sm">
        <Tippy
          content="przejdź do ekranu powitalnego"
          placement="right"
          theme="custom-dark"
        >
          <span
            className="text-6xl font-extrabold cursor-pointer"
            onClick={() => setShowingWelcomeScreen(true)}
          >
            ATE
          </span>
        </Tippy>
        <div className="relative border-2 border-dashed border-white rounded-lg items-center flex px-5 py-6 ml-auto mr-6 gap-5">
          <span className="absolute font-bold bg-base-300 -top-3 left-5 px-2 border-r">
            Dane do analizy:{' '}
          </span>
          <FileChanger
            currentFileName={getFileNameFromPath(projectData.file)}
            onChange={(projectData) => {
              setProjectData(projectData);
              const newSelectedSideFields =
                getDefaultSelectedFields(projectData);
              setSelectedSideFields(newSelectedSideFields);
              const newFilterParams = {
                column: projectData.filtering_field.default_value,
                value: getPossibleDependentOptions(
                  projectData.filtering_field.default_value,
                  projectData.filtering_field.dependent_field.values,
                )[0].value,
              };
              setFilterParams(newFilterParams);
              // whole data save in file needs resetting if any of the input parameters
              // changes (File, filter param1, filter param2)
              setMainPanelData(initialMainPanelData);
              if (isSidePanelDataSet(analysisMethod, newSelectedSideFields))
                fetchData(
                  visualizationTab,
                  analysisMethod,
                  newFilterParams,
                  newSelectedSideFields[analysisMethod],
                );
            }}
          />
          <form className="flex gap-5">
            <div className="flex items-center">
              <span
                className="mr-3 text-sm"
                id={`${filterFieldRenderingData.form_name}-label`}
              >
                {filterFieldRenderingData.label}:
              </span>
              <select
                name={filterFieldRenderingData.form_name}
                value={filterParams.column}
                onChange={(event) => {
                  const newFilterParams = {
                    column: event.target.value,
                    value: getPossibleDependentOptions(
                      event.target.value,
                      filterFieldRenderingData.dependent_field.values,
                    )[0].value,
                  };
                  setFilterParams(newFilterParams);
                  // whole data save in file needs resetting if any of the input parameters
                  // changes (File, filter param1, filter param2)
                  setMainPanelData(initialMainPanelData);
                  if (isSidePanelDataSet(analysisMethod, selectedSideFields))
                    fetchData(
                      visualizationTab,
                      analysisMethod,
                      newFilterParams,
                      selectedSideFields[analysisMethod],
                    );
                }}
                type="select"
                className="select select-sm w-[137px]"
                aria-labelledby={`${filterFieldRenderingData.form_name}-label`}
              >
                {filterFieldRenderingData.values.map(({ value, label }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <span
                className="mr-3 text-sm"
                id={`${filterFieldRenderingData.dependent_field.form_name}-label`}
              >
                {filterFieldRenderingData.dependent_field.label}:
              </span>
              <select
                name={filterFieldRenderingData.dependent_field.form_name}
                aria-labelledby={`${filterFieldRenderingData.dependent_field.form_name}-label`}
                value={filterParams.value}
                onChange={(event) => {
                  const newFilterParams = {
                    ...filterParams,
                    value: event.target.value,
                  };
                  setFilterParams(newFilterParams);
                  // whole data save in file needs resetting if any of the input parameters
                  // changes (File, filter param1, filter param2)
                  setMainPanelData(initialMainPanelData);
                  if (isSidePanelDataSet(analysisMethod, selectedSideFields))
                    fetchData(
                      visualizationTab,
                      analysisMethod,
                      newFilterParams,
                      selectedSideFields[analysisMethod],
                    );
                }}
                disabled={filterParams.column === ''}
                type="select"
                className={clsx('select select-sm w-24')}
              >
                {getPossibleDependentOptions(
                  filterParams.column,
                  filterFieldRenderingData.dependent_field.values,
                ).map(({ value, label }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </form>
          <DataPreviewer filterParams={filterParams} file={projectData?.file} />
        </div>
      </div>
      <div className="[&_img]:mt-0 w-full max-w-[--page-content-w] mx-auto border-black rounded-[--app-radius] mb-10">
        <div className="bg-base-300 flex px-6 gap-6 prose max-w-none border-t border-t-white">
          {/* side panel */}
          <div className="flex flex-col mb-6">
            <h2
              className="mt-12 mb-6"
              title="Metoda statystyczna stosowana do analizy"
              id="statistical-method-label"
            >
              Metoda statystyczna
            </h2>
            <Select
              defaultValue={analysisMethod}
              onChange={(newAnalysisMethod) => {
                setAnalysisMethod(newAnalysisMethod);
                if (!mainPanelData[newAnalysisMethod]) {
                  fetchData(
                    visualizationTab,
                    newAnalysisMethod,
                    filterParams,
                    selectedSideFields[newAnalysisMethod],
                  );
                }
              }}
              values={statisticalMethods}
              ariaLabelledBy="statistical-method-label"
            />
            <form className="flex flex-col max-w-72">
              <h2 className="mt-10 mb-0">Dostosuj</h2>
              {sideFieldsRenderingData?.map((field) => (
                <div
                  key={field.form_name}
                  className="flex flex-col items-stretch"
                >
                  <h3 id={`${field.form_name}-label`}>{field.label}</h3>
                  {field.type == 'select' && (
                    <Select
                      formName={field.form_name}
                      defaultValue={
                        selectedSideFields?.[analysisMethod]?.[
                          field.form_name
                        ] ?? field.default_value
                      }
                      values={field.values}
                      ariaLabelledBy={`${field.form_name}-label`}
                      onChange={(value) => {
                        const newSideFields = {
                          ...selectedSideFields,
                          [analysisMethod]: {
                            ...selectedSideFields[analysisMethod],
                            [field.form_name]: value,
                          },
                        };
                        setSelectedSideFields(newSideFields);
                        fetchData(
                          visualizationTab,
                          analysisMethod,
                          filterParams,
                          newSideFields[analysisMethod],
                        );
                      }}
                    />
                  )}
                  {field.type == 'multiselect' && (
                    <MultiSelect
                      formName={field.form_name}
                      values={field.values}
                      currentlySelected={
                        selectedSideFields?.[analysisMethod]?.[
                          field.form_name
                        ] ?? field.default_value
                      }
                      maxAllowed={field.max_allowed}
                      minAllowed={field.min_allowed}
                      ariaLabelledBy={`${field.form_name}-label`}
                      ariaLabel={field.label}
                      onChange={(value) => {
                        const newSelectedSideFields = {
                          ...selectedSideFields,
                          [analysisMethod]: {
                            ...selectedSideFields[analysisMethod],
                            [field.form_name]: value,
                          },
                        };
                        setSelectedSideFields(newSelectedSideFields);
                        fetchData(
                          visualizationTab,
                          analysisMethod,
                          filterParams,
                          newSelectedSideFields[analysisMethod],
                        );
                      }}
                    />
                  )}
                  {field.type == 'text' && (
                    <TextInput
                      formName={field.form_name}
                      defaultValue={
                        selectedSideFields?.[analysisMethod]?.[
                          field.form_name
                        ] ?? field.default_value
                      }
                      onChange={(value) => {
                        setSelectedSideFields((p) => ({
                          ...p,
                          [analysisMethod]: {
                            ...p[analysisMethod],
                            [field.form_name]: value,
                          },
                        }));
                        // text inputs are debounced so we don't fetch data here
                      }}
                    />
                  )}
                  {field.type == 'formula' && (
                    <EquationsField
                      values={field.values}
                      selectedValues={
                        selectedSideFields?.[analysisMethod]?.[field.form_name]
                      }
                      onChange={(value) => {
                        const newSelectedSideFields = {
                          ...selectedSideFields,
                          [analysisMethod]: {
                            [field.form_name]: value,
                          },
                        };
                        setSelectedSideFields(newSelectedSideFields);
                        setMainPanelData((p) => ({
                          ...p,
                          // need to reset mainPanelData for all LR
                          // tabs - if equationsField changes the data
                          // for all tabs becomes stale
                          line_regression: {
                            ...initialMainPanelData.line_regression,
                          },
                        }));
                        fetchData(
                          visualizationTab,
                          analysisMethod,
                          filterParams,
                          newSelectedSideFields[analysisMethod],
                        );
                      }}
                    />
                  )}
                </div>
              ))}
            </form>
          </div>
          {/* main panel */}
          <div className="border-l border-l-white pl-6 w-full pt-6 pb-6 relative">
            <div
              className={clsx(
                'transition-opacity duration-300',
                isLoading && 'opacity-40',
              )}
            >
              {analysisMethod !== 'line_regression' ? (
                isLoading && !mainPanelData[analysisMethod] ? (
                  <div className="flex items-center justify-center w-full h-[600px]">
                    <Loading />
                  </div>
                ) : isLoading && mainPanelData[analysisMethod] ? (
                  <>
                    <Loading
                      msg="Aktualizowanie wyników"
                      className="absolute inset-0 bg-base-300/60 backdrop-blur-sm z-10 my-0"
                    />
                    <NonLrComponent data={mainPanelData[analysisMethod]} />
                  </>
                ) : mainPanelData[analysisMethod] ? (
                  <NonLrComponent data={mainPanelData[analysisMethod]} />
                ) : (
                  // fallback for initial render when we're not yet loading,
                  // but still don't have data
                  <></>
                )
              ) : selectedSideFields.line_regression === null ||
                selectedSideFields.line_regression?.length === 0 ||
                (!mainPanelData.line_regression[visualizationTab] &&
                  !isLoading) ? (
                <div className="w-full h-full flex justify-center items-center">
                  <h2 className="text-info text-center">
                    Wprowadź formułę regresji liniowej prostej w panelu bocznym.
                    Wyniki wyświetlą się automatycznie.
                  </h2>
                </div>
              ) : (
                <>
                  <LineRegressionTabContent
                    data={mainPanelData.line_regression}
                    isLoading={isLoading}
                    visualizationTab={visualizationTab}
                    onTabChange={(newVisualizationTab) => {
                      setVisualizationTab(newVisualizationTab);
                      if (
                        !mainPanelData['line_regression'][newVisualizationTab]
                      ) {
                        fetchData(
                          newVisualizationTab,
                          'line_regression',
                          filterParams,
                          selectedSideFields['line_regression'],
                        );
                      }
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function getDefaultSelectedFields(projectData) {
  return {
    norm_statistic: Object.fromEntries(
      getStepDataFor(projectData?.steps, 'norm_statistic').map(
        ({ form_name, default_value }) => [form_name, default_value],
      ),
    ),
    qqplot: Object.fromEntries(
      getStepDataFor(projectData?.steps, 'qqplot').map(
        ({ form_name, default_value }) => [form_name, default_value],
      ),
    ),
    correlation: Object.fromEntries(
      getStepDataFor(projectData?.steps, 'correlation').map(
        ({ form_name, default_value }) => [form_name, default_value],
      ),
    ),
    line_regression: Object.fromEntries(
      getStepDataFor(projectData?.steps, 'line_regression').map(
        ({ form_name, default_value }) => [form_name, default_value],
      ),
    ),
  };
}

function adjustRequestParams(analysisMethod, params) {
  if (analysisMethod === 'line_regression') {
    params = {
      ...params,
      // equations are a more complicated structure and need to be sent as JSON
      equations: JSON.stringify(params.equations),
    };
  } else if (analysisMethod === 'correlation') {
    params = {
      ...params,
      // extract value from {value, label} pairs
      numerical_columns: params.numerical_columns.map(({ value }) => value),
    };
  }
  return params;
}

function getPossibleDependentOptions(
  selectedParent,
  allDependentValuesOptions,
) {
  return allDependentValuesOptions.find(
    (v) => v.dependent_on === selectedParent,
  )?.values;
}

function getStepDataFor(stepData, analysisMethod) {
  return stepData?.find((step) => step.step_name === analysisMethod)?.step_data;
}

function getFormSubmitUrl(analysisMethod, visualizationTab) {
  if (analysisMethod !== 'line_regression') {
    return {
      qqplot: window.DjUrls['ate:qqplot'](),
      correlation: window.DjUrls['ate:correlation'](),
      norm_statistic: window.DjUrls['ate:norm_statistic'](),
    }[analysisMethod];
  }
  return {
    plot: window.DjUrls['ate:line_regresion_plot'](),
    metrics: window.DjUrls['ate:line_regresion_metrics'](),
    assumptionTests: window.DjUrls['ate:line_regresion_assumptions_tests'](),
    evaluation: window.DjUrls['ate:line_regresion_evaluation'](),
  }[visualizationTab];
}

function getResponseType(analysisMethod, visualizationTab) {
  return analysisMethod === 'line_regression'
    ? visualizationTab === 'plot'
      ? 'blob'
      : 'json'
    : analysisMethod === 'norm_statistic'
      ? 'json'
      : 'blob';
}
